import { z } from 'zod'
import {
  LEAD_STATS_METRICS_MAX,
  LEAD_STATS_PIPELINES_MAX,
  appointmentsMetricDefinitionSchema,
  ghlNameSchema,
  leadStatsMetricDtoSchema,
  leadStatsPipelineDtoSchema,
  leadsMetricDefinitionSchema,
} from '../dto/lead-stats.dto.js'
import { normalizeGhlName } from '../helpers/lead-stats-names.js'
import { calendarDaySchema } from '../validators/temporal.validator.js'
import { leadStatsViewSchema } from '../enums/lead-stats-view.enum.js'

/**
 * Saving lead stats settings (docs/gohighlevel-stats.spec.md, "Numbers"). The
 * company's lists are saved whole, in order. Ids are the client's: a new number
 * gets one before it is saved, so a conversion in the same save can name it.
 */
export const putLeadStatsSettingsInputSchema = z
  .object({
    pipelines: z.array(leadStatsPipelineDtoSchema).max(LEAD_STATS_PIPELINES_MAX),
    metrics: z.array(leadStatsMetricDtoSchema).max(LEAD_STATS_METRICS_MAX),
    emailOff: z.boolean(),
  })
  .strict()
  .superRefine((value, ctx) => {
    repeatedNames(
      value.pipelines.map((p) => p.name),
      'Two pipelines can’t share a name.',
      ['pipelines'],
      ctx,
    )
    repeatedNames(
      value.metrics.map((m) => m.name),
      'Two numbers can’t share a name.',
      ['metrics'],
      ctx,
    )
    const ids = new Set<string>()
    for (const [i, p] of value.pipelines.entries()) {
      if (ids.has(p.id))
        ctx.addIssue({
          code: 'custom',
          message: 'A pipeline is listed twice.',
          path: ['pipelines', i],
        })
      ids.add(p.id)
    }
    const counted = new Set<string>()
    for (const [i, m] of value.metrics.entries()) {
      if (ids.has(m.id))
        ctx.addIssue({
          code: 'custom',
          message: 'A number is listed twice.',
          path: ['metrics', i],
        })
      ids.add(m.id)
      if (m.kind !== 'conversion') counted.add(m.id)
    }
    for (const [i, m] of value.metrics.entries()) {
      if (m.kind !== 'conversion') continue
      for (const side of ['numerator', 'denominator'] as const) {
        if (m.definition[side].some((id) => !counted.has(id))) {
          ctx.addIssue({
            code: 'custom',
            message: 'A conversion can only divide numbers in this list.',
            path: ['metrics', i, 'definition', side],
          })
        }
      }
    }
  })
export type PutLeadStatsSettingsInput = z.infer<typeof putLeadStatsSettingsInputSchema>

function repeatedNames(names: string[], message: string, path: (string | number)[], ctx: z.RefinementCtx): void {
  const seen = new Set<string>()
  for (const [i, name] of names.entries()) {
    const key = normalizeGhlName(name)
    if (seen.has(key)) ctx.addIssue({ code: 'custom', message, path: [...path, i, 'name'] })
    seen.add(key)
  }
}

/**
 * A number's definition as it is saved: strict, so a definition of one kind can't
 * pass for another with its keys stripped. (Responses can't be strict; see
 * `response-shapes.test.ts`.)
 */
export const leadsMetricDefinitionInputSchema = leadsMetricDefinitionSchema.strict()
export const appointmentsMetricDefinitionInputSchema = appointmentsMetricDefinitionSchema.strict()

/**
 * A venue's adjustments, saved whole. A null list or definition means the venue
 * goes back to the company's. Whether a definition suits its number's kind is
 * checked by the API, which knows the kind.
 */
export const putVenueLeadStatsAdjustmentsInputSchema = z
  .object({
    pipelines: z
      .array(
        z
          .object({
            pipelineId: z.string().uuid(),
            ghlPipelineNames: z.array(ghlNameSchema).min(1).max(100).nullable(),
          })
          .strict(),
      )
      .max(LEAD_STATS_PIPELINES_MAX),
    metrics: z
      .array(
        z
          .object({
            metricId: z.string().uuid(),
            definition: z.union([appointmentsMetricDefinitionInputSchema, leadsMetricDefinitionInputSchema]).nullable(),
          })
          .strict(),
      )
      .max(LEAD_STATS_METRICS_MAX),
  })
  .strict()
export type PutVenueLeadStatsAdjustmentsInput = z.infer<typeof putVenueLeadStatsAdjustmentsInputSchema>

/** Which pipeline, view and period a venue's stats are read for. Each defaults when left out. */
export const leadStatsQuerySchema = z
  .object({
    pipeline: z.string().uuid().optional(),
    view: leadStatsViewSchema.optional(),
    period: calendarDaySchema.optional(),
  })
  .strict()
export type LeadStatsQuery = z.infer<typeof leadStatsQuerySchema>
